
import React, { useEffect, useMemo, useState } from "react";
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx'
import { Button, FormCheck } from "react-bootstrap";
import Select from 'react-select';
import { BasicFetch } from "../../../utils/BasicFetch.ts";
import { CollectionReptile } from "../types/CollectionTypes.ts";
import { LoadingSpinner } from "../../Loading.tsx";
import { Reptile } from "../../../types/Reptile.ts";
import { driver } from "driver.js";
import { ContactModal } from "../../utilities/ContactModal.tsx";
import { useRepToolContext } from '../../../RepToolContext.tsx';
type Props = {
  closeModal: () => void;
  reptiles: Reptile[];
};

interface RadioFilterValue {
  value: string;
  label: string;
}

const statuses = ["Available", "NFS", "Sold"];
const sexes = ["Male", "Female", "Unsexed"];


const RadioFilters = ({ label, options, selected, onChange }) => {
  const [allSelected, setAllSelected] = useState(true);

  useEffect(() => {
    if (!allSelected) {
      document.getElementById(`${label}-multi-select`)?.click();
    }
  })

  return (
    <div className="d-flex flex-column">
      <div className={`flex flex-row ${allSelected ? '' : 'text-dark'}`}>
        <FormCheck type='radio' label={`All ${label}`} name={label} checked={!allSelected} onChange={() => setAllSelected(false)} />
      </div>
      <div className={`d-flex mb-3 flex-column ${allSelected ? 'text-dark' : ''}`} onClick={() => {
        setAllSelected(false);
      }}>

        <FormCheck className='mb-2' type='radio' label={`Custom ${label}`} name={label} checked={!allSelected} onChange={() => setAllSelected(false)} />
        <Select
          id={`${label}-multi-select`}
          options={options}
          isMulti
          value={allSelected ? [] : selected}
          onChange={onChange}
          isDisabled={allSelected}
          classNamePrefix={`${label} label-multi-select`}
        />
      </div>
    </div>
  );
}
interface LabelTemplate {
  name: string;
  value: string;
}

const IframeModal = ({ url, closeModal, extras }: { url: string, closeModal: () => void, extras: any }) => {
  const [loading, setLoading] = useState(false);
  const [pageSizes, setPageSizes] = useState<string[]>([]);
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<LabelTemplate | null>(null);
  const [selectedPageSize, setSelectedPageSize] = useState<string | null>(null);
  const [requestContactQuestion, setRequestContactQuestion] = useState<string | null>(null);
  const { appendControls } = useRepToolContext();

  let searchParams = new URLSearchParams({ ...extras, api_key: localStorage.getItem("reptoolCookie") });
  const pageSpecString = useMemo(() => {
    return `template=${selectedTemplate?.value}&page_size=${selectedPageSize}`;
  }, [selectedTemplate, selectedPageSize]);
  const iframeUrl = `${url}?${searchParams}&${pageSpecString}`;
  const downloadUrl = `${iframeUrl}&download=true&${pageSpecString}`;
  const getPageSizeOptions = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/labels/get_spec`,
      method: "GET",
      onSuccess: (data) => {
        setSelectedPageSize(data.page_sizes[0]);
        setSelectedTemplate(data.templates[0]);
        setPageSizes(data.page_sizes);
        setTemplates(data.templates);
      }
    });
  }

  useEffect(() => {
    getPageSizeOptions();
    appendControls(null);
  }, []);
  const isCordova = useMemo(() => {
    return (window as any).cordova;
  }, []);
  return (
    <>
      {requestContactQuestion && <ContactModal
        preselectedQuestion={requestContactQuestion}
        onSend={() => setRequestContactQuestion(null)}
        closeModal={() => setRequestContactQuestion(null)}
        informationText="Please enter the template you would like to request. If you have a specific size or layout in mind, please include that information as well. Or if the template you are looking for isn't displayed well on the preview, please let me know."
      />}
      <ReptoolModal id="iframe-label-modal" title={loading ? "Generating labels..." : "Generated Labels"} closeModal={closeModal} body={
        <div className="container">
          <div className="my-3">
            <div>
              <h5>Page Size:</h5>
              <select className="form-control bg-dark text-light" value={selectedPageSize || undefined} onChange={(e) => setSelectedPageSize(e.target.value)}>
                {pageSizes.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mt-3">
              <h5>Template:</h5>
              <select className="form-control bg-dark text-light" value={selectedTemplate?.value} onChange={(e) => {
                const selectedTemplate = templates.find((t) => t.value === e.target.value);
                setSelectedTemplate(selectedTemplate || null);
              }}>
                {templates.map((t) => <option key={t.value} value={t.value}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <p>
            Below is a preview of the labels that will be generated. Click the printer icon in the top right to print or use the print labels button at the bottom of the page.
            (You may need to wait a few seconds for the preview to finish loading)
          </p>
          <p>
            If you have any issues with the preview, you can also download the labels as a PDF to print later.
          </p>
          {selectedPageSize && selectedTemplate && <Button onClick={() => {
            if (isCordova && cordova.InAppBrowser) {
              cordova.InAppBrowser.open(downloadUrl, '_system', 'location=yes');
              return;
            } else {
              window.open(downloadUrl, '_blank');
            }
          }} variant="outline-light" className="mb-3 mx-auto">Download PDF</Button>}


          <div className="my-3">
            <p>
              Don't see the template you're looking for? Or have an issue with the generated labels? Contact the developer to add more templates or fix the issue.
            </p>
            <Button
              variant="outline-light"
              onClick={() => setRequestContactQuestion("The label template I am looking for isn't listed.")}
            >
              Request Template
            </Button>
            <Button
              variant="outline-light"
              onClick={() => setRequestContactQuestion("I am having issues with the generated labels.")}
              className="ml-2"
            >
              Report Issue
            </Button>
          </div>

          {
            loading &&
            <div className="d-flex flex-column justify-content-center text-center">
              <LoadingSpinner />
            </div>
          }

          {selectedPageSize && selectedTemplate && <iframe id="label-preview" src={iframeUrl} style={{ width: "100%", height: "50vh" }} className={`${loading ? 'd-none' : ''}`} onLoad={() => setLoading(false)} />}
        </div>
      } />
    </>

  )
}



export const LabelModal = ({ closeModal, reptiles }: Props) => {
  const [selectedReptiles, setSelectedReptiles] = useState<RadioFilterValue[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<RadioFilterValue[]>([]);
  const [selectedSexes, setSelectedSexes] = useState<RadioFilterValue[]>([]);
  const [showIframe, setShowIframe] = useState(false);
  const [results, setResults] = useState<CollectionReptile[]>([]);
  const [loading, setLoading] = useState(false);
  const { appendControls } = useRepToolContext();

  const idList = results.map((r) => r.id).join(",");
  const driverObjLabelModal = driver({
    steps: [
      {
        popover: {
          title: 'Create Labels',
          description: 'Select the reptiles you want to generate labels for. These labels can be used for many purposes such as the "scan and record" feature. Alternatively, these labels can also be scanned by other users to quickly view the reptile\'s information or inquire to buy if the animal is available and you have sharing turned on.',
        }
      },
      {
        element: '#get-labels-btn',
        popover: {
          title: 'Get Labels',
          description: 'Click here to generate labels for the reptiles that match the selected filters.',
        }
      },
      {
        element: '#labels-filter',
        popover: {
          title: 'Filter Options',
          description: 'Use thse filters to select which reptiles you want to generate labels for.',
        }
      }
    ]
  });


  useEffect(() => {
    if (showIframe) {
      appendControls(null);
      return;
    }

    appendControls(<>
      <Button id='get-labels-btn' className="mx-auto px-4" onClick={() => setShowIframe(true)} variant='outline-light'>
        Get Labels
      </Button>
    </>);
  }, [results, showIframe]);

  const checkCount = () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("reptile_ids", selectedReptiles.map((r) => r.value));
    formData.append("statuses", selectedStatuses.map((s) => s.value));
    formData.append("sexes", selectedSexes.map((s) => s.value));

    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/get_with_filters`,
      method: "POST",
      data: formData,
      contentType: 'multipart/form-data',
      onSuccess: (data) => {
        setResults(data.results);
      },
      onFinish: () => {
        setLoading(false);
      }
    })
  }

  useEffect(() => {
    checkCount();
  }, [selectedReptiles, selectedStatuses, selectedSexes]);


  const url = `${(window as any).serverLocation}/collection_reptile/labels`;
  const extras = {
    id_list: idList
  }

  let explainationSentence = useMemo(() => {
    let baseSentence = "This will generate labels for ";
    if (selectedReptiles.length === 0) {
      baseSentence += "all reptiles";
    } else {
      baseSentence += selectedReptiles.map((r) => r.label).join(", ");
    }
    baseSentence += " that are ";
    if (selectedStatuses.length === 0) {
      baseSentence += "any status";
    } else {
      baseSentence += selectedStatuses.map((s) => s.label).join(", ");
    }
    baseSentence += " and are ";
    if (selectedSexes.length === 0) {
      baseSentence += "any sex"
    } else {
      baseSentence += selectedSexes.map((s) => s.label).join(", ");
    }
    return baseSentence;
  }, [selectedReptiles, selectedStatuses, selectedSexes]);

  return (
    <>
      {showIframe && <IframeModal url={url} closeModal={() => setShowIframe(false)} extras={extras} />}
      <ReptoolModal id='label-modal' showTutorial={() => driverObjLabelModal.drive()} title="Create Labels" closeModal={closeModal} body={
        <div id='labels-filter' className="container">
          <div className="mb-3">
            <RadioFilters
              label="Reptiles"
              options={reptiles.map((r) => ({ value: r.id, label: r.reptile_name }))}
              selected={selectedReptiles}
              onChange={(selected) => setSelectedReptiles(selected.map((s) => s))}
            />
          </div>
          <div className="mb-3">
            <RadioFilters
              label="Statuses"
              options={statuses.map((s) => ({ value: s, label: s }))}
              selected={selectedStatuses}
              onChange={(selected) => setSelectedStatuses(selected.map((s) => s))}
            />
          </div>
          <div className="mb-3">
            <RadioFilters
              label="Sexes"
              options={sexes.map((s) => ({ value: s, label: s }))}
              selected={selectedSexes}
              onChange={(selected) => setSelectedSexes(selected.map((s) => s))}
            />
          </div>
          <div className="mb-3">
            <p>{explainationSentence}.</p>
          </div>
          <div>
            {loading ? <h6>Fetching Result Count</h6> : <h6>Total Reptiles: {results.length}</h6>}
          </div>
        </div>
      } />
    </>

  );
};

